
/* eslint-disable @typescript-eslint/camelcase */
import { Options, Vue } from "vue-class-component";
import { formatDate, downloadFile } from "@/app/infrastructures/misc/Utils";
import { BulkBookingController } from "@/app/ui/controllers/BulkBookingController";
import { PosClientController } from "@/app/ui/controllers/PosClientController";
import { debounce } from "lodash";
import { AccountController } from "@/app/ui/controllers/AccountController";
import {
  resolvePosDistrictCountryCode,
  bulkTemplateUrl,
  permissionBulkBooking,
  woodpackingFlow
} from "./create-bulk-booking/modules/utils";
import {
  FlagsBulkDownloadTemplate
} from "@/feature-flags/flags-bulk-download-template";
import AdvanceFilter from "./components/advance-filter.vue";
import {
  downloadFailedBulkBooking,
  downloadSuccessBulkBooking,
  getValueOrString,
  listBulkBooking,
  setBulkBookingListFirstPage
} from "./list-bulk-booking-utils";
import JsBarcode from "jsbarcode";
import moment from "moment";
import generateThermalPDF from "@/app/ui/modules/receipt-v2/bulk-resi-thermal";
import router from "@/app/ui/router";

const RETAIL_DOMESTIC_TEMPLATE_NAMES = [
  "bulkBooking.common.retailDomestic",
  "bulkBooking.create.templateRetailDomestic"
];

const DOWNLOAD_TEMPLATE_NAMES_EN: Record<string, string> = {
  "bulkBooking.common.retailInternational": "Retail International",
  "bulkBooking.common.retailDomestic": "Retail Domestic",
  "bulkBooking.common.clientInternational": "Client International",
  "bulkBooking.common.clientDomestic": "Client Domestic",
  "bulkBooking.create.templateRetailInternational":
    "Retail international booking",
  "bulkBooking.create.templateRetailDomestic": "Retail domestic booking",
  "bulkBooking.create.templateClientInternational":
    "Client international booking",
  "bulkBooking.create.templateClientDomestic": "Client domestic booking"
};

@Options({
  components: {
    AdvanceFilter
  }
})
class BulkBookingV2 extends Vue {
  startDate: any = null;
  endDate: any = null;
  showDownloadMenu = false;

  get listBookingUtils() {
    return listBulkBooking;
  }

  mounted() {
    this.startDate = this.startDate
      ? moment()
          .add(-1, "month")
          .toString()
      : "";
    this.endDate = this.endDate ? new Date().toString() : "";
    this.fetchPosClient("");
    setBulkBookingListFirstPage();

    if (this.isClient) {
      this.filterData.createdFor = {
        id: this.dataProfile.account_type_detail.id,
        type: this.dataProfile.account_type
      };
      this.filterData.formType = { 
        name: this.$t("bulkBooking.filter.typeClient") as string,
        value: "client" 
      };
    }
    this.onRequestFetchBulkBookingList();
  }

  fetchPosClient = debounce((search) => {
    if (search.length > 2 || !search) {
      PosClientController.getPosClientList(search);
    }
  });

  get getCurrentRoute() {
    return this.$route.meta.customName;
  }

  // search filter
  searchValue = "";
  onSearch(value: any) {
    this.searchValue = value;
    setBulkBookingListFirstPage();
    this.onRequestFetchBulkBookingList();
  }
  clearSearch() {
    this.searchValue = "";
    this.onRequestFetchBulkBookingList();
  }

  // date

  setPeriode(value: Array<any>) {
    this.startDate = value[0];
    this.endDate = value[1];
    setBulkBookingListFirstPage();
    this.onRequestFetchBulkBookingList();
  }

  filterData: any = {
    createdFor: "",
    uploadStatus: "",
    formType: ""
  };

  onChangeAdvancedFilter(event: any) {
    this.filterData = event;
    if (event.startDate !== undefined) {
      this.startDate = event.startDate;
      this.endDate = event.endDate;
    }
    setBulkBookingListFirstPage();
    this.onRequestFetchBulkBookingList();
  }
  onResetAdvancedFilter() {
    this.startDate = "";
    this.endDate = "";
    if (this.isClient) {
      this.filterData.uploadStatus = "";
    } else {
      this.filterData = {
        createdFor: "",
        uploadStatus: "",
        formType: ""
      };
    }
    setBulkBookingListFirstPage();
    this.onRequestFetchBulkBookingList();
  }

  onRequestFetchBulkBookingList() {
    BulkBookingController.fetchBulkBookingList({
      search: this.searchValue,
      createdId: getValueOrString(this.filterData.createdFor?.id),
      createdType: getValueOrString(this.filterData.createdFor?.type),
      status: getValueOrString(this.filterData.uploadStatus?.value),
      startDate: this.startDate ? new Date(this.startDate).toISOString() : "",
      endDate: this.endDate ? new Date(this.endDate).toISOString() : "",
      bookedType: getValueOrString(this.filterData.formType?.value),
      archiveType: "stt-v2"
    });
  }

  get bulkDownloadBucketStorage() {
    const bulkFlag =
      FlagsBulkDownloadTemplate.bulk_booking_download_template_options;
    const options = bulkFlag.getValue();
    if (!Array.isArray(options) || woodpackingFlow() !== "v2") return options;

    return options.map((option) => {
      if (!RETAIL_DOMESTIC_TEMPLATE_NAMES.includes(option.name)) return option;
      return { ...option, url: this.domesticTemplateUrl };
    });
  }

  downloadTemplateName(name: string): string {
    return DOWNLOAD_TEMPLATE_NAMES_EN[name] || name;
  }

  get errorCause() {
    return BulkBookingController.errorCause;
  }

  get isEmpty() {
    return (
      !BulkBookingController.status &&
      !this.listBookingUtils().dataTable.length &&
      this.listBookingUtils().isFirstRequest &&
      !this.listBookingUtils().isLoading
    );
  }
  // Pagination Table
  get pagination() {
    return BulkBookingController.bulkBookingList.pagination;
  }
  // Table
  get columns() {
    const uploadIdColumn = {
      name: this.$t("bulkBooking.columnUploadId"),
      styleHead: "w-24 text-left whitespace-no-wrap text-sm",
      headerId: "booking-bulk-v2-list-col-upload-id",
      render: (item: any) => {
        return `<div class='text-left text-sm text-black-lp-300 flex justify-center break-words line-clamp-2'>${item.uploadId}</div>`;
      }
    };

    const statusColumn = {
      name: this.$t("bulkBooking.columnStatus"),
      styleHead: "w-48 text-left whitespace-no-wrap text-sm",
      headerId: "booking-bulk-v2-list-col-status",
      render: (item: any) => {
        if (item.status.toLowerCase() === "success") {
          return `<div class="flex flex-col text-center font-medium text-sm capitalize">
                    <div class="rounded-full px-2 py-0 bg-green-lp-300 text-green-lp-400 w-max">${this.mapUpdateStatusLabel(
                      item
                    )}</div>
                    ${
                      item.totalSttRepaired
                        ? `<div class="mt-1 text-gray-lp-800 text-12px">${
                            item.totalSttRepaired
                          } ${this.$t("bulkBooking.repairedCount")}</div>`
                        : ""
                    }
                  </div>`;
        } else if (item.status.toLowerCase() === "failed") {
          return `<div class="flex flex-col font-medium text-sm text-center capitalize">
                    <div class="rounded-full px-2 py-0 bg-red-lp-600 text-red-lp-1500 w-max">${this.mapUpdateStatusLabel(
                      item
                    )}</div>
                    ${
                      item.totalSttRepaired
                        ? `<div class="mt-1 text-gray-lp-800 text-12px">${
                            item.totalSttRepaired
                          } ${this.$t("bulkBooking.repairedCount")}</div>`
                        : ""
                    }
                  </div>`;
        } else if (item.status.toLowerCase() === "uploading") {
          return `<div class="flex font-medium text-sm text-center capitalize">
                    <div class="rounded-full px-2 py-0 bg-gray-lp-200 text-gray-lp-300">${this.mapUpdateStatusLabel(
                      item
                    )}</div>
                  </div>`;
        }
        return `<div class="flex flex-col font-medium text-sm text-center capitalize">
                    <div class="rounded-full px-2 py-0 bg-yellow-lp-200 text-yellow-lp-100 w-max">${this.mapUpdateStatusLabel(
                      item
                    )}</div>
                    ${
                      item.totalSttRepaired
                        ? `<div class="mt-1 text-gray-lp-800 text-12px">${
                            item.totalSttRepaired
                          } ${this.$t("bulkBooking.repairedCount")}</div>`
                        : ""
                    }
                </div>`;
      }
    };

    const createdForColumn = {
      name: this.$t("bulkBooking.columnDibuatUntuk"),
      styleHead: "w-56 text-left whitespace-no-wrap text-sm",
      headerId: "booking-bulk-v2-list-col-created-for",
      render: (item: any) => {
        return `<div class='overflow-ellipsis line-clamp-1 text-sm text-black-lp-300'>${item.createdFor}</div>
        <div class='flex text-sm text-gray-lp-500 text-12px'>${item.createdForCode}</div>`;
      }
    };

    const createdAtColumn = {
      name: this.$t("bulkBooking.columnTanggalDibuat"),
      styleHead: "w-56 text-left whitespace-no-wrap text-sm",
      headerId: "booking-bulk-v2-list-col-created-at",
      render: (item: any) => {
        return `<span class="text-black-lp-300 text-sm">${formatDate(
          item.createdAt
        )}</span>
        <div class='flex text-sm text-gray-lp-500 text-12px'>${
          item.createdBy
        }</div>`;
      }
    };

    if (this.isClient) {
      return [
        uploadIdColumn,
        {
          name: this.$t("bulkBooking.columnTipeBooking"),
          styleHead: "w-32 text-left whitespace-no-wrap text-sm",
          headerId: "booking-bulk-v2-list-col-booking-type",
          render: (item: any) => {
            return `<div data-testid="booking-bulk-v2-list-cell-booking-type-${item.uploadId}" class="overflow-ellipsis text-sm text-black-lp-300 break-words line-clamp-2">${item.bookingType}</div>`;
          }
        },
        {
          name: this.$t("bulkBooking.columnNamaFile"),
          styleHead: "w-36 text-left whitespace-no-wrap text-sm",
          headerId: "booking-bulk-v2-list-col-file-name",
          render: (item: any) => {
            return `<div title="${item.fileName}" class="overflow-ellipsis text-sm text-left break-words line-clamp-2 underline text-red-lp-100">${item.fileName}</div>`;
          }
        },
        statusColumn,
        {
          name: this.$t("bulkBooking.columnCatatan"),
          styleHead: "w-36 text-left whitespace-no-wrap text-sm",
          headerId: "booking-bulk-v2-list-col-note",
          render: (item: any) => {
            return `<div data-testid="booking-bulk-v2-list-cell-note-${item.uploadId}" class="overflow-ellipsis text-sm text-black-lp-300 break-words line-clamp-2">${item.activityName}</div>`;
          }
        },
        createdForColumn,
        createdAtColumn,
        {
          name: this.$t("bulkBooking.columnAtur"),
          key: "button_column",
          styleHead: "w-20 text-left",
          headerId: "booking-bulk-v2-list-col-actions",
          styleButton: (item: any) => {
            return this.buildAturStyleButton(item);
          }
        }
      ];
    }

    return [
      createdAtColumn,
      uploadIdColumn,
      {
        name: this.$t("bulkBooking.columnNamaAktivitas"),
        styleHead: "w-36 text-left whitespace-no-wrap text-sm",
        render: (item: any) => {
          return `<div class="overflow-ellipsis text-sm text-black-lp-300 break-words line-clamp-2">${item.activityName}</div>`;
        }
      },
      {
        name: this.$t("bulkBooking.columnTipeBerkas"),
        styleHead: "w-32 text-left whitespace-no-wrap text-sm",
        render: (item: any) => {
          return `<div class="overflow-ellipsis text-sm capitalize text-black-lp-300 break-words line-clamp-2">${item.createdType}</div>`;
        }
      },
      {
        name: this.$t("bulkBooking.columnNamaBerkas"),
        styleHead: "w-36 text-left whitespace-no-wrap text-sm",
        render: (item: any) => {
          return `<div title="${item.fileName}" class="overflow-ellipsis text-sm text-left break-words line-clamp-2 underline text-red-lp-100">${item.fileName}</div>`;
        }
      },
      statusColumn,
      createdForColumn,
      {
        name: this.$t("bulkBooking.columnAtur"),
        key: "button_column",
        styleHead: "w-20 text-left",
        styleButton: (item: any) => {
          return this.buildAturStyleButton(item);
        }
      }
    ];
  }

  buildAturStyleButton(item: any) {
    if (this.disableSetting(item)) {
      return {
        outline: true,
        icon: "option-gray",
        title: "",
        textColor: "black",
        color: "gray-lp-400",
        small: false,
        groupOptionsClass: "pickup-manifest-group-options",
        customClass: `w-full bg-gray-lp-1400`
      };
    } else if (item.status.toLowerCase() === "success") {
      return {
        outline: true,
        icon: "option-gray",
        title: "",
        textColor: "black",
        color: "gray-lp-400",
        small: false,
        groupOptionsClass: "pickup-manifest-group-options",
        customClass: `w-full`,
        clickFunction: () => {
          true;
        },
        groupOptions: [
          {
            icon: "download-new-gray",
            label: this.$t("bulkBooking.downloadSuccessFile"),
            sizeIcon: "18px",
            clickFunction: () => {
              downloadSuccessBulkBooking(item.uploadId);
            }
          },
          {
            icon: "download-new-gray",
            label: this.$t("bulkBooking.downloadOriginalFile"),
            sizeIcon: "18px",
            clickFunction: () => {
              this.downloadOriginalBulkBooking(item.archiveFile);
            }
          },
          {
            icon: "printer",
            label: this.$t("bulkBooking.printStt"),
            sizeIcon: "18px",
            clickFunction: () => {
              this.printResiA4(item.uploadId);
            }
          }
        ]
      };
    } else if (item.status.toLowerCase() === "partially") {
      const partiallyGroupOptions = [
        {
          icon: "ics_o_package_failed",
          label: this.$t("bulkBooking.repairFailedBooking"),
          dataTestid: `booking-bulk-v2-list-action-repair-failed-${item.uploadId}`,
          sizeIcon: "18px",
          clickFunction: () => {
            this.goToRepair(item.uploadId);
          }
        },
        {
          icon: "download-new-gray",
          label: this.$t("bulkBooking.downloadSuccessFile"),
          sizeIcon: "18px",
          clickFunction: () => {
            downloadSuccessBulkBooking(item.uploadId);
          }
        },
        {
          icon: "download-new-gray",
          label: this.$t("bulkBooking.downloadFailedFile"),
          sizeIcon: "18px",
          clickFunction: () => {
            downloadFailedBulkBooking(item.uploadId);
          }
        },
        {
          icon: "download-new-gray",
          label: this.$t("bulkBooking.downloadOriginalFile"),
          sizeIcon: "18px",
          clickFunction: () => {
            this.downloadOriginalBulkBooking(item.archiveFile);
          }
        },
        {
          icon: "printer",
          label: this.$t("bulkBooking.printStt"),
          sizeIcon: "18px",
          clickFunction: () => {
            this.printResiA4(item.uploadId);
          }
        }
      ];

      return {
        outline: true,
        icon: "option-gray",
        title: "",
        textColor: "black",
        color: "gray-lp-400",
        small: false,
        groupOptionsClass: "pickup-manifest-group-options",
        customClass: `w-full`,
        clickFunction: () => {
          true;
        },
        groupOptions: partiallyGroupOptions
      };
    }

    const failedGroupOptions = [
      {
        icon: "ics_o_package_failed",
        label: this.$t("bulkBooking.repairFailedBooking"),
        dataTestid: `booking-bulk-v2-list-action-repair-failed-${item.uploadId}`,
        sizeIcon: "18px",
        clickFunction: () => {
          this.goToRepair(item.uploadId);
        }
      },
      {
        icon: "download-new-gray",
        label: this.$t("bulkBooking.downloadFailedFile"),
        sizeIcon: "18px",
        clickFunction: () => {
          downloadFailedBulkBooking(item.uploadId);
        }
      },
      {
        icon: "download-new-gray",
        label: this.$t("bulkBooking.downloadOriginalFile"),
        sizeIcon: "18px",
        clickFunction: () => {
          this.downloadOriginalBulkBooking(item.archiveFile);
        }
      }
    ];

    return {
      outline: true,
      icon: "option-gray",
      title: "",
      textColor: "black",
      color: "gray-lp-400",
      small: false,
      groupOptionsClass: "pickup-manifest-group-options",
      customClass: `w-full`,
      clickFunction: () => {
        true;
      },
      groupOptions: failedGroupOptions
    };
  }

  disableSetting(item: any) {
    return item.status.toLowerCase() === "uploading";
  }

  mapUpdateStatusLabel(item: any): any {
    const statusLabel: any = (status: string) => {
      const obj = {
        success: this.$t("bulkBooking.statusSuccess"),
        failed: this.$t("bulkBooking.statusFailed"),
        partially: this.$t("bulkBooking.statusPartially"),
        uploading: this.$t("bulkBooking.statusUploading")
      } as any;
      return obj[status] ?? status;
    };
    return `${statusLabel(item.status.toLowerCase())} ${item.totalSttSuccess}/${
      item.totalStt
    }`;
  }

  get dataProfile() {
    return AccountController.accountData;
  }

  get isInternal() {
    return this.dataProfile.account_type === "internal";
  }

  get isClient() {
    return this.dataProfile.account_type === "client";
  }

  get permissionClient() {
    return permissionBulkBooking().permissionClient;
  }

  get isPos() {
    return this.dataProfile.isPosAccount;
  }

  shouldShowDownloadTemplateBtn(): boolean {
    return this.isClient || this.isPos;
  }

  get internationalTemplateUrl(): string {
    const countryCode = this.isPos
      ? resolvePosDistrictCountryCode(
          this.dataProfile.account_type_detail.partnerLocation
        )
      : this.dataProfile.countryCode;

    return bulkTemplateUrl({
      scope: "international",
      countryCode
    });
  }

  get domesticTemplateUrl(): string {
    const countryCode = this.dataProfile?.countryCode;
    return bulkTemplateUrl({
      scope: "domestic",
      countryCode
    });
  }

  downloadTemplate(name: string, url: string) {
    this.showDownloadMenu = false;

    downloadFile(url, name, ".xlsx").catch(() => undefined);
  }

  goToUploadBulkBooking() {
    this.$router.push("/shipment/bulk/bulk-v2/create");
  }

  downloadOriginalBulkBooking(archiveFile: any) {
    if (archiveFile) {
      window.open(archiveFile, "_blank");
    }
  }

  // print pdf resi a4
  async printResiA4(sttId: number) {
    const print = await this.generatePrintData(sttId);
    if (print) {
      this.printDoc();
    }
  }
  async generatePrintData(sttId: number) {
    return BulkBookingController.getBulkBookingResi({ id: sttId });
  }

  async printDoc() {
    const listSttPieces: any[] = [];
    const barcodeList: any[] = [];
    const vendorRefCodeList: any[] = [];

    BulkBookingController.resiListData.forEach(async (item, index) => {
      const barcode = document.createElement("img");
      const vendorBarcode = document.createElement("img");
      // barcode
      this.printBarcode(barcodeList, item, barcode);

      // vendor ref code
      this.printVendorRefCode(vendorRefCodeList, vendorBarcode, item);

      item.sttPieces.forEach((e) => {
        listSttPieces.push({
          ...e,
          ...item,
          barcode: barcodeList[index].barcode,
          vendorRefCode: vendorRefCodeList[index].vendorRefCode
        });
      });

      if (index === BulkBookingController.resiListData.length - 1) {
        await generateThermalPDF(
          this.listBookingUtils().accountType,
          listSttPieces
        );
      }
    });
  }

  printBarcode(barcodeList: any[], item: any, barcode: any) {
    JsBarcode(barcode, item.sttElexysNo || item.sttNo, {
      textAlign: "left",
      displayValue: false,
      fontSize: 16
    });

    let barcodeValue = "";
    if (barcodeList.length > 0) {
      barcodeList.forEach((val) => {
        if (val.sttNo === item.sttElexysNo || val.sttNo === item.sttNo)
          barcodeValue = val.barcode;
        else barcodeValue = barcode.src;
      });
    } else barcodeValue = barcode.src;

    barcodeList.push({
      sttNo: item.sttElexysNo || item.sttNo,
      barcode: barcodeValue
    });
  }

  printVendorRefCode(vendorRefCodeList: any[], vendorBarcode: any, item: any) {
    if (item.sttVendorRefferenceNo) {
      JsBarcode(vendorBarcode, item.sttVendorRefferenceNo, {
        textAlign: "left",
        displayValue: false,
        fontSize: 16
      });
    }

    let vendorRefCodeValue = "";
    if (vendorRefCodeList.length > 0) {
      vendorRefCodeList.forEach((val) => {
        if (val.sttVendorRefferenceNo === item.sttVendorRefferenceNo)
          vendorRefCodeValue = val.vendorRefCode;
        else vendorRefCodeValue = vendorBarcode.src;
      });
    } else vendorRefCodeValue = vendorBarcode.src;

    vendorRefCodeList.push({
      sttVendorRefferenceNo: item.sttVendorRefferenceNo,
      vendorRefCode: vendorRefCodeValue
    });
  }

  goToRepair(id: number) {
    router.push(`/shipment/bulk/bulk-v2/repair-bulk?id=${id}`);
  }
}

export default BulkBookingV2;
